import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Save, Loader2, Plus, Type, Heading2, List, Trash2, ArrowUp, ArrowDown,
} from "lucide-react";
import { Logo } from "../../components/marketing/Logo";
import { adminApi, getToken, formatApiError } from "./api";

const slugify = (t) =>
    t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s]+/g, "-").replace(/-+/g, "-").slice(0, 80);

const BLOCK_TYPES = [
    { id: "p", label: "Paragraph", icon: Type },
    { id: "h2", label: "Heading", icon: Heading2 },
    { id: "list", label: "Bullet list", icon: List },
];

const emptyForm = {
    title: "",
    slug: "",
    excerpt: "",
    category: "Sales Process",
    author: "Team Kryvo",
    read_minutes: 5,
    published_at: new Date().toISOString().slice(0, 10),
    status: "draft",
};

const serializeBlocks = (blocks) =>
    blocks
        .map((b) => {
            if (b.type === "list") {
                const items = b.text.split("\n").map((s) => s.trim()).filter(Boolean);
                return items.length ? { type: "list", items } : null;
            }
            return b.text.trim() ? { type: b.type, text: b.text.trim() } : null;
        })
        .filter(Boolean);

const deserializeBlocks = (content) =>
    (content || []).map((b) =>
        b.type === "list" ? { type: "list", text: (b.items || []).join("\n") } : { type: b.type, text: b.text || "" },
    );

export default function AdminEditor() {
    const { id } = useParams();
    const isNew = !id;
    const navigate = useNavigate();
    const [form, setForm] = useState(emptyForm);
    const [blocks, setBlocks] = useState([{ type: "p", text: "" }]);
    const [slugTouched, setSlugTouched] = useState(false);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await adminApi().get("/admin/blog/posts");
            const post = data.find((p) => p.id === id);
            if (!post) {
                toast.error("Article not found");
                navigate("/admin", { replace: true });
                return;
            }
            setForm({
                title: post.title, slug: post.slug, excerpt: post.excerpt,
                category: post.category, author: post.author,
                read_minutes: post.read_minutes, published_at: post.published_at,
                status: post.status,
            });
            setBlocks(deserializeBlocks(post.content));
            setSlugTouched(true);
        } catch (err) {
            if (err?.response?.status === 401) navigate("/admin/login", { replace: true });
            else toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        document.title = isNew ? "New Article | Kryvo Admin" : "Edit Article | Kryvo Admin";
        if (!getToken()) {
            navigate("/admin/login", { replace: true });
            return;
        }
        if (!isNew) load();
    }, [isNew, load, navigate]);

    const set = (key) => (e) => {
        const v = e.target.value;
        setForm((f) => ({
            ...f,
            [key]: v,
            ...(key === "title" && !slugTouched ? { slug: slugify(v) } : {}),
        }));
    };

    const setBlock = (i, patch) =>
        setBlocks((bs) => bs.map((b, bi) => (bi === i ? { ...b, ...patch } : b)));

    const moveBlock = (i, dir) =>
        setBlocks((bs) => {
            const j = i + dir;
            if (j < 0 || j >= bs.length) return bs;
            const next = [...bs];
            [next[i], next[j]] = [next[j], next[i]];
            return next;
        });

    const save = async (e) => {
        e.preventDefault();
        const content = serializeBlocks(blocks);
        if (!content.length) {
            toast.error("Add at least one content block.");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, read_minutes: Number(form.read_minutes), content };
            if (isNew) await adminApi().post("/admin/blog/posts", payload);
            else await adminApi().put(`/admin/blog/posts/${id}`, payload);
            toast.success(isNew ? "Article created." : "Article saved.");
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#16A34A]";
    const labelCls = "text-xs font-semibold text-[#334155]";

    if (loading) {
        return (
            <p className="flex min-h-screen items-center justify-center gap-2 bg-[#F7F9FA] text-sm text-[#94A3B8]">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading article…
            </p>
        );
    }

    return (
        <div data-testid="admin-editor-page" className="min-h-screen bg-[#F7F9FA]">
            <header className="border-b border-[#E5E7EB] bg-white">
                <div className="mx-auto flex h-16 w-full max-w-4xl items-center justify-between px-4 sm:px-6">
                    <Link to="/"><Logo /></Link>
                    <Link to="/admin" className="flex items-center gap-1.5 text-xs font-semibold text-[#64748B] hover:text-[#15803D]">
                        <ArrowLeft className="h-3.5 w-3.5" /> All articles
                    </Link>
                </div>
            </header>

            <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
                <h1 className="font-display text-2xl font-extrabold text-[#111827]">
                    {isNew ? "New article" : "Edit article"}
                </h1>

                <form onSubmit={save} className="mt-8 grid gap-6">
                    <div className="grid gap-5 rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
                        <div className="grid gap-1.5">
                            <label className={labelCls} htmlFor="f-title">Title</label>
                            <input id="f-title" data-testid="editor-input-title" required minLength={4} className={inputCls} value={form.title} onChange={set("title")} placeholder="How growing teams close more deals" />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-slug">Slug (URL)</label>
                                <input id="f-slug" data-testid="editor-input-slug" required pattern="[a-z0-9]+(-[a-z0-9]+)*" className={inputCls} value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug")(e); }} placeholder="growing-teams-close-deals" />
                                <p className="text-[11px] text-[#94A3B8]">Public URL: /blog/{form.slug || "…"}</p>
                            </div>
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-category">Category</label>
                                <input id="f-category" data-testid="editor-input-category" required className={inputCls} value={form.category} onChange={set("category")} placeholder="Sales Process" />
                            </div>
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-date">Publish date</label>
                                <input id="f-date" data-testid="editor-input-date" required type="date" className={inputCls} value={form.published_at} onChange={set("published_at")} />
                            </div>
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-read">Read time (minutes)</label>
                                <input id="f-read" data-testid="editor-input-read-minutes" required type="number" min={1} max={90} className={inputCls} value={form.read_minutes} onChange={set("read_minutes")} />
                            </div>
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-author">Author</label>
                                <input id="f-author" data-testid="editor-input-author" required className={inputCls} value={form.author} onChange={set("author")} />
                            </div>
                            <div className="grid gap-1.5">
                                <label className={labelCls} htmlFor="f-status">Status</label>
                                <select id="f-status" data-testid="editor-select-status" className={inputCls} value={form.status} onChange={set("status")}>
                                    <option value="draft">Draft — hidden from public blog</option>
                                    <option value="published">Published — live on /blog</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid gap-1.5">
                            <label className={labelCls} htmlFor="f-excerpt">Excerpt</label>
                            <textarea id="f-excerpt" data-testid="editor-input-excerpt" required minLength={10} rows={2} className={inputCls} value={form.excerpt} onChange={set("excerpt")} placeholder="One or two sentences shown on the blog listing and in link previews." />
                        </div>
                    </div>

                    {/* Content blocks */}
                    <div className="grid gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-[#111827]">Content</p>
                            <p className="text-[11px] text-[#94A3B8]">Bullet lists: one item per line</p>
                        </div>
                        {blocks.map((b, i) => (
                            <div key={i} className="rounded-xl border border-[#EDF0F3] bg-[#FBFDFC] p-3.5">
                                <div className="mb-2 flex items-center gap-1.5">
                                    {BLOCK_TYPES.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setBlock(i, { type: t.id })}
                                            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                                                b.type === t.id ? "bg-[#16A34A] text-white" : "border border-[#E5E7EB] bg-white text-[#64748B] hover:text-[#15803D]"
                                            }`}
                                        >
                                            <t.icon className="h-3 w-3" /> {t.label}
                                        </button>
                                    ))}
                                    <span className="ml-auto flex items-center gap-1">
                                        <button type="button" aria-label="Move up" onClick={() => moveBlock(i, -1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#94A3B8] hover:text-[#15803D]">
                                            <ArrowUp className="h-3 w-3" />
                                        </button>
                                        <button type="button" aria-label="Move down" onClick={() => moveBlock(i, 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#94A3B8] hover:text-[#15803D]">
                                            <ArrowDown className="h-3 w-3" />
                                        </button>
                                        <button type="button" aria-label="Delete block" data-testid={`editor-delete-block-${i}`} onClick={() => setBlocks((bs) => bs.filter((_, bi) => bi !== i))} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#94A3B8] hover:border-red-200 hover:text-red-600">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </span>
                                </div>
                                <textarea
                                    rows={b.type === "h2" ? 1 : 4}
                                    data-testid={`editor-block-text-${i}`}
                                    className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#16A34A]"
                                    placeholder={b.type === "h2" ? "Section heading" : b.type === "list" ? "One bullet per line" : "Write a paragraph…"}
                                    value={b.text}
                                    onChange={(e) => setBlock(i, { text: e.target.value })}
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            data-testid="editor-add-block"
                            onClick={() => setBlocks((bs) => [...bs, { type: "p", text: "" }])}
                            className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#16A34A]/40 py-3 text-sm font-semibold text-[#15803D] transition-colors hover:bg-[#F0FDF4]"
                        >
                            <Plus className="h-4 w-4" /> Add block
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="submit"
                            data-testid="editor-save-button"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-full bg-[#16A34A] px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#15803D] disabled:opacity-60"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {saving ? "Saving…" : form.status === "published" ? "Save & publish" : "Save draft"}
                        </button>
                        <Link to="/admin" className="rounded-full border border-[#E5E7EB] bg-white px-6 py-3 text-sm font-semibold text-[#475569] hover:text-[#15803D]">
                            Cancel
                        </Link>
                    </div>
                </form>
            </main>
        </div>
    );
}
